<template>
  <!-- 有下级，用el-sub-menu，无下级用el-menu-item -->
  <template v-for="item in menuList" :key="item.path">
    <!-- 非叶子节点 v-show：true(显示)false(隐藏)，v-if反之。 -->
    <el-sub-menu v-if="item.children?.length" :index="item.path">
      <template #title>
        <el-icon v-if="item.meta.icon && item.meta.icon.indexOf(SVG_PREFIX) == -1">
          <component :is="item.meta.icon"></component>
        </el-icon>
        <!-- 本地SVG -->
        <el-icon v-if="item.meta.icon && item.meta.icon.indexOf(SVG_PREFIX) == 0">
          <component :is="'SvgIcon'" :name="item.meta.icon"></component>
        </el-icon>
        <span v-text="item.meta.title"></span>
      </template>
      <AsideSubMenu :menu-list="item.children" />
    </el-sub-menu>
    <!-- 叶子节点（功能节点） -->
    <el-menu-item v-else :index="item.path" @click="handleMenuRouter(item)">
      <el-icon v-if="item.meta.icon && item.meta.icon.indexOf(SVG_PREFIX) == -1">
        <component :is="item.meta.icon"></component>
      </el-icon>
      <!-- 本地SVG -->
      <el-icon v-if="item.meta.icon && item.meta.icon.indexOf(SVG_PREFIX) == 0">
        <component :is="'SvgIcon'" :name="item.meta.icon"></component>
      </el-icon>
      <template #title>
        <span v-text="item.meta.title"></span>
      </template>
    </el-menu-item>
  </template>
</template>

<script setup lang="ts">
import AsideSubMenu from "@/layouts/components/Menu/AsideSubMenu.vue";
import { SVG_PREFIX } from "@/config/index";
import { lcMsgWarning } from "@/utils/util";
import { useRouter } from "vue-router";
import useUserStore from "@/stores/modules/user";
import useTaskStore from "@/stores/modules/task";

type Props = {
  menuList: { [k: string]: any }[];
};

const router = useRouter();
// 获取父组件传递过来的数据
defineProps<Props>();

/* 打开标签页 或 外部链接 */
const handleMenuRouter = (value: any) => {
  if (value.meta?.isLink) {
    if (/^https?:\/\//.test(value.meta?.isLink)) {
      const userStore = useUserStore();
      const url = `${value.meta.isLink}?tk=${userStore.token}`;
      return window.open(url, "_blank");
    } else {
      lcMsgWarning("非正确链接地址，禁止跳转");
      return;
    }
  }
  if (typeof value.meta?.formType !== "undefined" && value.meta?.formType) {
    const taskStore = useTaskStore();
    if (value.meta?.formType === 1) {
      taskStore.setSubtaskId(value.meta?.subtaskId);
      taskStore.setSubtaskWhereConfig(value.meta?.whereConfig);
      router.push({
        path: value.path,
        query: {
          id: value.meta?.subtaskId,
        },
      });
    } else if (value.meta?.formType === 2) {
      taskStore.setCollectionTaskId(value.meta?.collectionTaskId);
      taskStore.setSubtaskWhereConfig(value.meta?.whereConfig);
      router.push({
        path: value.path,
        query: {
          id: value.meta?.collectionTaskId,
        },
      });
    } else {
      router.push(value.path);
    }
    return;
  }
  if (value.meta?.subtaskId) {
    const taskStore = useTaskStore();
    taskStore.setSubtaskId(value.meta?.subtaskId);
    taskStore.setSubtaskWhereConfig(value.meta?.whereConfig);
    router.push({
      path: value.path,
      query: {
        id: value.meta?.subtaskId,
      },
    });
    return;
  }
  router.push(value.path);
};
</script>

<style lang="scss" scoped>
/** el-menu鼠标悬停和点击代码 */
// 父节点
.el-menu-item {
  height: $aside-menu-height !important;
  margin-bottom: $aside-menu-margin-bottom;
  font-weight: $aside-menu-font-weight;
  color: #e5eaf3;

  /* 设置用户禁止选中 */
  user-select: none;
  border-left: $aside-menu-border-left solid transparent;
  border-radius: $aside-menu-border-left;

  @apply dark:c-#E5E3FA;
  i {
    transform: translate($aside-menu-font-icon-translate); // 图标偏移
  }
  span {
    transform: translate($aside-menu-font-icon-translate); // 文字偏移
  }

  // 设置鼠标悬停时el-menu-item的样式
  &:hover {
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-8);
    border-left: $aside-menu-border-left solid var(--el-color-primary);

    // & 含义 .el-menu-item
    border-radius: $aside-menu-border-left;

    // 实现鼠标悬停时icon变色
    i {
      color: var(--el-color-primary);
    }
  }

  // 设置选中el-menu-item时的样式
  &.is-active {
    color: var(--el-menu-active-color);
    background: var(--el-color-primary-light-8);
    border-left: $aside-menu-border-left solid var(--el-color-primary);
  }
}

// 子节点
:deep(.el-sub-menu__title) {
  height: $aside-menu-height;
  padding-right: 0; // 去除collapse缩小多余的边框
  margin-bottom: $aside-menu-margin-bottom;
  font-weight: $aside-menu-font-weight;
  color: #e5eaf3;

  /* 设置用户禁止选中 */
  user-select: none;
  border-left: $aside-menu-border-left solid transparent;
  border-radius: $aside-menu-border-left;

  @apply dark:c-#E5E3FA;
  i {
    transform: translate($aside-menu-font-icon-translate); // 图标偏移
  }
  span {
    transform: translate($aside-menu-font-icon-translate); // 文字偏移
  }
  &:hover {
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-8);
    border-left: $aside-menu-border-left solid var(--el-color-primary);
  }
  &:active {
    color: var(--el-menu-active-color-primary);
    background: var(--el-color-primary-light-8);
    border-left: $aside-menu-border-left solid var(--el-color-primary);
  }
}
</style>

<style lang="scss">
/* 子级菜单字体高亮，父级菜单也高亮 */
.el-sub-menu.is-active > .el-sub-menu__title {
  color: var(--el-menu-active-color) !important;
}

/* icon图标也跟着变 */
.el-sub-menu.is-active > .el-sub-menu__title i {
  color: var(--el-menu-active-color) !important;
}
</style>
